const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_MONTH = /^\d{4}-\d{2}$/;

/**
 * Returns today as YYYY-MM-DD in the given IANA zone. The whole feature compares dates as strings
 * so that "past / today / future" never depends on the browser timezone.
 */
export function todayInZone(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA').format(new Date());
  }
}

/** Extracts the YYYY-MM month from a YYYY-MM-DD date. */
export function monthOf(date: string): string {
  return ISO_DATE.test(date) ? date.slice(0, 7) : date;
}

/** Shifts a YYYY-MM month by `delta` months, wrapping the year. */
export function addMonths(month: string, delta: number): string {
  if (!ISO_MONTH.test(month)) {
    return month;
  }
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7)) - 1 + delta;
  const shiftedYear = year + Math.floor(index / 12);
  const shiftedMonth = ((index % 12) + 12) % 12;
  return `${String(shiftedYear).padStart(4, '0')}-${String(shiftedMonth + 1).padStart(2, '0')}`;
}

/** Number of days in a YYYY-MM month. */
export function daysInMonth(month: string): number {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7));
  return new Date(Date.UTC(year, index, 0)).getUTCDate();
}

/** Weekday column (0 = Monday) of the first day of a YYYY-MM month. */
export function firstWeekdayOfMonth(month: string): number {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(5, 7)) - 1;
  return (new Date(Date.UTC(year, index, 1)).getUTCDay() + 6) % 7;
}

/** Builds the YYYY-MM-DD date for a day number inside a YYYY-MM month. */
export function dateOf(month: string, day: number): string {
  return `${month}-${String(day).padStart(2, '0')}`;
}

/** Inclusive list of YYYY-MM-DD dates between two dates, ordered ascending. */
export function datesBetween(from: string, to: string): string[] {
  const [start, end] = from <= to ? [from, to] : [to, from];
  const dates: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor.getTime() <= last.getTime()) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

/** Shifts a YYYY-MM-DD date by `delta` days. */
export function shiftDate(date: string, delta: number): string {
  const cursor = new Date(`${date}T00:00:00Z`);
  cursor.setUTCDate(cursor.getUTCDate() + delta);
  return cursor.toISOString().slice(0, 10);
}

/** The first and last night of a month, which is the span the grid reads for it. */
export function monthSpan(month: string): { from: string; to: string } {
  return { from: dateOf(month, 1), to: dateOf(month, daysInMonth(month)) };
}
